import { useEffect, useRef } from "react";
import { postSearchLog } from "@/lib/search-log";

const CONFIRMED_SEARCH_DELAY_MS = 1500;

interface UseSearchLogTimerOptions {
  query: string;
  resultCount: number | null;
  source: "search" | "suggest";
  initialQuery?: string;
}

/**
 * 検索ログの確定判定を行うカスタムフック。
 *
 * 入力後 1.5 秒間追加入力がなければ「滞在判定」としてログを記録し、
 * Enter 押下時には即時記録する。lastLoggedQuery による重複防止と、
 * initialQuery によるページ遷移時の二重記録防止を内包する。
 *
 * @param options.query - 現在の検索クエリ（trimmed）
 * @param options.resultCount - 検索結果件数（不明なら null）
 * @param options.source - 検索経路（"search" | "suggest"）
 * @param options.initialQuery - 重複防止の初期値（遷移元で既に記録済みのクエリ）
 * @returns logOnSubmit - Enter/フォーム送信時に呼ぶ即時記録関数
 * @returns cancelTimer - 入力変更時にタイマーをキャンセルする関数
 */
export function useSearchLogTimer({
  query,
  resultCount,
  source,
  initialQuery,
}: UseSearchLogTimerOptions): {
  logOnSubmit: (overrideQuery?: string) => void;
  cancelTimer: () => void;
} {
  // 直近でログ記録したクエリを保持する。同一クエリの重複記録を防ぐために使う。
  // useState ではなく useRef を使う理由: 値の変更で再レンダリングを起こしたくないため。
  // initialQuery が指定されていれば「遷移元で既に記録済み」とみなし、初期値にセットする。
  const lastLoggedQuery = useRef<string>(initialQuery ?? "");

  // 滞在判定タイマー（setTimeout）の参照を保持する。
  // useRef を使う理由: useEffect のクリーンアップや logOnSubmit から
  // タイマーをキャンセルするために、レンダリングをまたいで参照を保持する必要がある。
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 最新の resultCount を保持する。
  // useRef を使う理由: useEffect の依存配列に resultCount を含めると、
  // 検索結果件数が変わるたびにタイマーがリセットされてしまう（BUG-1 の原因）。
  // ref で最新値を追跡し、タイマー発火時にその時点の値を読み取る。
  const resultCountRef = useRef<number | null>(resultCount);

  // 初回マウントかどうかを追跡するフラグ。
  // 初回マウント時は lastLoggedQuery をリセットせず initialQuery の値を維持するために使う。
  const isFirstMount = useRef(true);

  // レンダリングのたびに resultCountRef を最新の props 値で更新する。
  // これにより setTimeout のコールバック内で常に最新の件数を参照できる。
  resultCountRef.current = resultCount;

  // --- 滞在判定タイマーの管理 ---
  // query が変化するたびに実行される。前回のタイマーをクリアし、新しいタイマーを起動する。
  useEffect(() => {
    // query が変わったので、前回のタイマーが残っていればキャンセルする。
    // これにより「入力中はタイマーが走らない」動作を実現する。
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // 空クエリではログを記録しない（検索窓をクリアした場合など）
    if (!query) return;

    if (isFirstMount.current) {
      isFirstMount.current = false;
      // 初回マウント時: lastLoggedQuery を initialQuery のまま維持する。
      // initialQuery がセットされていれば「遷移元で同じクエリを記録済み」なので、
      // タイマー発火時に lastLoggedQuery === query となりスキップされる。
      // initialQuery が未指定（ダイレクトアクセス等）なら lastLoggedQuery は "" なので、
      // タイマー発火時に記録が実行される。
    } else {
      // 2回目以降の query 変化: ユーザーが新しいキーワードを入力した。
      // lastLoggedQuery をリセットして、新しいクエリのログ記録を許可する。
      lastLoggedQuery.current = "";
    }

    // 1.5 秒後に発火する滞在判定タイマーをセットする。
    // ユーザーが 1.5 秒間追加入力しなければ「検索結果を見ている」と判定する。
    timerRef.current = setTimeout(() => {
      // 既に同じクエリでログ記録済みならスキップ（重複防止）
      if (lastLoggedQuery.current === query) return;

      void postSearchLog({
        query,
        // resultCountRef.current で「この瞬間の最新の件数」を取得する。
        // クロージャに閉じ込めた古い値ではなく、ref 経由で最新値を読む。
        resultCount: resultCountRef.current,
        source,
      }).catch(console.error);

      // 記録済みクエリを更新。同じクエリでの再記録を防ぐ。
      lastLoggedQuery.current = query;
    }, CONFIRMED_SEARCH_DELAY_MS);

    // useEffect のクリーンアップ関数: コンポーネントのアンマウント時、
    // または次の useEffect 実行前にタイマーを破棄する。
    // これにより、アンマウント後にログが送信されることを防ぐ。
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [query, source]);

  /**
   * 滞在判定タイマーをキャンセルする。
   * 入力変更時に呼び出し、前回のタイマーを破棄して新しい入力を待つ。
   */
  function cancelTimer() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  /**
   * Enter 押下・フォーム送信時に即時でログを記録する。
   * 滞在判定タイマーが動作中であればキャンセルする。
   * lastLoggedQuery と一致する場合は重複防止のためスキップする。
   *
   * @param overrideQuery - debounce 完了前のクエリを即時記録したい場合に指定
   */
  function logOnSubmit(overrideQuery?: string) {
    // overrideQuery が指定されていればそちらを使う。
    // SearchInput では debounce 完了前の入力値を即時記録するために使用する。
    const targetQuery = overrideQuery ?? query;
    if (!targetQuery) return;

    // 直近で同じクエリを記録済みならスキップ（滞在判定で記録 → Enter の重複を防ぐ）
    if (lastLoggedQuery.current === targetQuery) return;

    void postSearchLog({
      query: targetQuery,
      resultCount: resultCountRef.current,
      source,
    }).catch(console.error);

    // 記録済みクエリを更新
    lastLoggedQuery.current = targetQuery;

    // Enter で即時記録したので、滞在判定タイマーは不要。キャンセルして二重記録を防ぐ。
    cancelTimer();
  }

  return { logOnSubmit, cancelTimer };
}
